import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  TextField,
  Typography,
  Select,
  MenuItem,
  Box,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Badge,
  Fab,
} from "@mui/material";
import { Add, ArrowBack, FilterList, Close, Refresh, FilterAlt, Edit } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import EditDelete from "../../../../components/EditDelete";
import { useDispatch } from "react-redux";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../../redux/slices/snackbar";
import useInventory from "../../../../hooks/useInventory";
import axios from "../../../../utils/axios";
import DatePicker from "react-multi-date-picker";
import ToolBar from "../../../../components/Toolbar";
import { saveFile } from "../../../../utils/common";

function ReturnSection() {
  const {
    getAllReturn,
    addReturn,
    updateReturn,
    autoSearchUser,
    exportInventoryReport,
    isLoading,
  } = useInventory();
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [isAdding, setIsAdding] = useState(false);
  const [materials, setMaterials] = useState([]);

  // Export State
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadType, setDownloadType] = useState("");

  // User Autocomplete State
  const [userOptions, setUserOptions] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form State
  const [returnNo, setReturnNo] = useState("");
  const [returnDate, setReturnDate] = useState(new Date().toISOString().split("T")[0]);
  const [remarks, setRemarks] = useState("");
  const [items, setItems] = useState([{ materialId: "", returnQuantity: 1 }]);
  const [editingId, setEditingId] = useState(null);

  // Filter State
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterReturnDate, setFilterReturnDate] = useState("");
  const [filterUserName, setFilterUserName] = useState("");
  const [filterMaterialName, setFilterMaterialName] = useState("");

  // Temp/Modal Filter State
  const [tempReturnDate, setTempReturnDate] = useState("");
  const [tempUserName, setTempUserName] = useState("");
  const [tempMaterialName, setTempMaterialName] = useState("");

  const fetchReturns = async (
    date = filterReturnDate,
    user = filterUserName,
    material = filterMaterialName,
  ) => {
    try {
      const activeFilters = [];
      if (date) {
        activeFilters.push({
          fieldName: "returnDate",
          fieldValue: date,
          op: "eq",
          dataType: "date",
          condition: "and",
          isJoin: false,
        });
      }
      if (user.trim()) {
        activeFilters.push({
          fieldName: "userName",
          fieldValue: user.trim(),
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }
      if (material.trim()) {
        activeFilters.push({
          fieldName: "materialName",
          fieldValue: material.trim(),
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }

      const payload = {
        pagination: { pageNumber: paginationModel.page + 1, pageSize: paginationModel.pageSize },
        filters: activeFilters,
        sortFields: [{ field: "returnId", sort: "desc" }],
      };
      const res = await getAllReturn(payload);
      if (res) {
        setRows(res.data || []);
        setRowCount(res.paging?.totalNumberOfRecords || 0);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchMaterials = async () => {
    try {
      const res = await axios.post("/material/list", {
        pagination: { pageNumber: 1, pageSize: 1000 },
        filters: [],
        sortFields: [],
      });
      setMaterials(res.data?.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchReturns();
  }, [paginationModel, filterReturnDate, filterUserName, filterMaterialName]);

  useEffect(() => {
    if (isAdding) {
      fetchMaterials();
    }
  }, [isAdding]);

  const handleUserSearch = async (val) => {
    if (!val || val.length < 2) return;
    setUserLoading(true);
    try {
      const results = await autoSearchUser(val);
      setUserOptions(results);
    } catch (e) {
      console.error(e);
    } finally {
      setUserLoading(false);
    }
  };

  const handleItemChange = (index, field, value) => {
    const updated = items.map((item, idx) => {
      if (idx === index) {
        return { ...item, [field]: value };
      }
      return item;
    });
    setItems(updated);
  };

  const dispatch = useDispatch();

  const handleEdit = (row) => {
    setReturnNo(row.returnNo || "");
    setReturnDate(
      row.returnDate ? row.returnDate.split(" ")[0] : new Date().toISOString().split("T")[0],
    );
    setSelectedUser(row.userId ? { radUserId: row.userId, userName: row.userName } : null);
    setRemarks(row.remarks || "");
    setItems(
      row.items && row.items.length > 0
        ? row.items.map((i) => ({
            materialId: i.materialId,
            returnQuantity: i.returnQuantity,
          }))
        : [{ materialId: "", returnQuantity: 1 }],
    );
    setEditingId(row.returnId);
    setIsAdding(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      dispatch(showErrorSnackbar("User is required"));
      return;
    }
    const hasInvalidItem = items.some((item) => !item.materialId || item.returnQuantity <= 0);
    if (hasInvalidItem) {
      dispatch(showErrorSnackbar("Please select a material and enter a valid quantity."));
      return;
    }

    try {
      const payload = {
        returnNo,
        returnDate,
        userId: parseInt(selectedUser.radUserId),
        remarks,
        items: items.map((i) => ({
          materialId: parseInt(i.materialId),
          returnQuantity: parseFloat(i.returnQuantity),
        })),
      };
      if (editingId) {
        await updateReturn(editingId, payload);
        dispatch(showSuccessSnackbar("Material return updated successfully!"));
      } else {
        await addReturn(payload);
        dispatch(showSuccessSnackbar("Material returned successfully!"));
      }
      setIsAdding(false);
      setEditingId(null);
      fetchReturns();
      // Reset form
      setReturnNo("");
      setSelectedUser(null);
      setRemarks("");
      setItems([{ materialId: "", returnQuantity: 1 }]);
    } catch (err) {
      dispatch(showErrorSnackbar(err.message || "Failed to save return material"));
    }
  };

  const handleDownloadFile = async (fileType) => {
    setDownloadType(fileType);
    setIsDownloading(true);
    try {
      const activeFilters = [];
      if (filterReturnDate) {
        activeFilters.push({
          fieldName: "returnDate",
          fieldValue: filterReturnDate,
          op: "eq",
          dataType: "date",
          condition: "and",
          isJoin: false,
        });
      }
      if (filterUserName.trim()) {
        activeFilters.push({
          fieldName: "userName",
          fieldValue: filterUserName.trim(),
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }
      if (filterMaterialName.trim()) {
        activeFilters.push({
          fieldName: "materialName",
          fieldValue: filterMaterialName.trim(),
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }
      const payload = {
        filters: activeFilters,
      };

      const resBlob = await exportInventoryReport("return", fileType, payload);
      const filename = `return_list_${new Date().toISOString().slice(0, 10)}.${fileType}`;
      await saveFile(resBlob, filename);
      dispatch(showSuccessSnackbar(`Report exported successfully as ${fileType.toUpperCase()}!`));
    } catch (err) {
      console.error(err);
      dispatch(showErrorSnackbar("Failed to export report"));
    } finally {
      setIsDownloading(false);
      setDownloadType("");
    }
  };

  const handleApplyFilter = () => {
    setFilterReturnDate(tempReturnDate);
    setFilterUserName(tempUserName);
    setFilterMaterialName(tempMaterialName);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setIsFilterOpen(false);
  };

  const handleResetFilter = () => {
    setFilterReturnDate("");
    setFilterUserName("");
    setFilterMaterialName("");
    setTempReturnDate("");
    setTempUserName("");
    setTempMaterialName("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setIsFilterOpen(false);
  };

  const handleRefresh = () => {
    fetchReturns();
  };

  const activeFiltersCount =
    (filterReturnDate ? 1 : 0) + (filterUserName ? 1 : 0) + (filterMaterialName ? 1 : 0);

  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <EditDelete
          rowId={params.row.returnId}
          editPage={() => handleEdit(params.row)}
          menus={["edit"]}
        />
      ),
    },
    { field: "returnNo", headerName: "Return No", width: 180 },
    { field: "returnDate", headerName: "Return Date", width: 180 },
    { field: "userName", headerName: "User", width: 220 },
    {
      field: "materialName",
      headerName: "Material Name",
      width: 220,
      valueGetter: (value, row) => {
        if (!row?.items || row.items.length === 0) return "";
        return row.items
          .map((item) => item.materialName || "")
          .filter(Boolean)
          .join(", ");
      },
    },
    {
      field: "totalQuantity",
      headerName: "Total Quantity",
      width: 160,
      type: "number",
      valueGetter: (value, row) => {
        if (!row?.items) return 0;
        return row.items.reduce((sum, item) => sum + (item.returnQuantity || 0), 0);
      },
    },
    { field: "remarks", headerName: "Remarks", width: 300 },
  ];

  if (isAdding) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h5">{editingId ? "Edit Return" : "Return Material"}</Typography>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => {
              setIsAdding(false);
              setEditingId(null);
              setReturnNo("");
              setSelectedUser(null);
              setRemarks("");
              setItems([{ materialId: "", returnQuantity: 1 }]);
            }}
          >
            Back to List
          </Button>
        </Box>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <DatePicker
                containerStyle={{ width: "100%" }}
                portal
                zIndex={10000}
                value={returnDate}
                onChange={(date) => setReturnDate(date ? date.format("YYYY-MM-DD") : "")}
                format="YYYY/MM/DD"
                render={
                  <TextField
                    fullWidth
                    label="Return Date"
                    required
                    InputLabelProps={{ shrink: !!returnDate }}
                  />
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                options={userOptions}
                getOptionLabel={(option) => option.userName || ""}
                loading={userLoading}
                value={selectedUser}
                onChange={(e, newVal) => setSelectedUser(newVal)}
                onInputChange={(e, val) => handleUserSearch(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search User"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {userLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {items.map((item, index) => (
              <React.Fragment key={index}>
                <Grid item xs={12} md={6}>
                  <Select
                    fullWidth
                    value={item.materialId}
                    onChange={(e) => handleItemChange(index, "materialId", e.target.value)}
                    displayEmpty
                    required
                  >
                    <MenuItem value="" disabled>
                      Select Material
                    </MenuItem>
                    {materials.map((m) => (
                      <MenuItem key={m.materialId} value={m.materialId}>
                        {m.materialName} ({m.uom})
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Return Quantity"
                    inputProps={{ min: "1", step: "1" }}
                    value={item.returnQuantity}
                    onChange={(e) => handleItemChange(index, "returnQuantity", e.target.value)}
                    onWheel={(e) => e.target.blur()}
                    required
                  />
                </Grid>
              </React.Fragment>
            ))}

            <Grid item xs={12} md={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading}
                size="large"
              >
                {isLoading ? "Saving..." : "Save Return"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h5">Material Return Log</Typography>
        <Box display="flex" gap={2} alignItems="center">
          <ToolBar
            addPage={() => setIsAdding(true)}
            refreshPage={handleRefresh}
            openFilterDialog={() => setIsFilterOpen(true)}
            downloadFile={handleDownloadFile}
            isLoadingDownload={isDownloading}
            downloadFileType={downloadType}
          />
        </Box>
      </Box>
      <div style={{ height: 500, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.returnId}
          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={[10, 25, 50]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          loading={isLoading}
          disableRowSelectionOnClick
        />
      </div>

      {/* Advance Filter Dialog */}
      <Dialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6">Advance Filter</Typography>
          <IconButton onClick={() => setIsFilterOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="User"
                value={tempUserName}
                onChange={(e) => setTempUserName(e.target.value)}
                placeholder="Search user name"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Material Name"
                value={tempMaterialName}
                onChange={(e) => setTempMaterialName(e.target.value)}
                placeholder="Search material name"
              />
            </Grid>
            <Grid item xs={12}>
              <DatePicker
                containerStyle={{ width: "100%" }}
                portal
                zIndex={10000}
                range
                value={tempReturnDate ? tempReturnDate.split(",") : []}
                onChange={(dates) => {
                  if (dates && dates.length > 0) {
                    const formatted = dates.map((d) => d.format("YYYY-MM-DD")).join(",");
                    setTempReturnDate(formatted);
                  } else {
                    setTempReturnDate("");
                  }
                }}
                format="YYYY/MM/DD"
                render={
                  <TextField
                    fullWidth
                    label="Return Date"
                    variant="outlined"
                    InputLabelProps={{ shrink: !!tempReturnDate }}
                  />
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleResetFilter} variant="outlined" startIcon={<Refresh />}>
            Reset
          </Button>
          <Button
            onClick={handleApplyFilter}
            variant="contained"
            color="primary"
            startIcon={<FilterAlt />}
          >
            filter
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ReturnSection;
